<template>
  <div class="flex w-full items-start gap-5 py-4">
    <nuxt-link
      class="block size-32.5 shrink-0"
      :to="productUrl"
      @click="closeCart"
    >
      <UiLazyImg
        :src="product.image_url || '/images/no-image.webp'"
        :alt="product.title || product.currentTranslation?.title"
      />
    </nuxt-link>
    <div class="flex flex-auto flex-col self-stretch overflow-hidden">
      <div class="mb-1.5 flex w-full gap-2.5">
        <div class="inline-flex flex-auto overflow-hidden">
          <nuxt-link
            class="flex w-full items-start gap-2.5 font-gt-ultra-fine text-sm"
            :to="productUrl"
            @click="closeCart"
          >
            <span
              data-e2e-item="cart-product-title"
            >
              {{ product.title }}
            </span>
            <UiButton
              v-if="product.configuration_id && !hideMenu && wishlistInTitle"
              variant-type="icon"
              variant="clear"
              class="size-auto shrink-0 p-0 max-lg:ml-auto"
              :aria-label="$t('addToWishlist')"
              @click.stop.prevent="
                updateWishlist({
                  sku: product.configuration_id,
                  vendor_model: product.vendor_model,
                })
              "
            >
              <UiIcon
                name="heart"
                class="size-3.5"
                :class="[
                  inWishlist({
                    sku: product.configuration_id,
                    vendor_model: product.vendor_model,
                  })
                    ? 'fill-gray-90'
                    : '',
                ]"
                aria-hidden="true"
              />
            </UiButton>
          </nuxt-link>
        </div>
        <div v-if="!hideMenu && !wishlistInTitle" class="flex-shrink-0">
          <UiButton
            v-if="product.configuration_id"
            variant-type="icon"
            variant="clear"
            :aria-label="$t('addToWishlist')"
            @click="
              updateWishlist({
                sku: product.configuration_id,
                vendor_model: product.vendor_model,
              })
            "
          >
            <UiIcon
              name="heart"
              class="size-3.5"
              :class="[
                inWishlist({
                  sku: product.configuration_id,
                  vendor_model: product.vendor_model,
                })
                  ? 'fill-gray-90'
                  : '',
              ]"
              aria-hidden="true"
            />
          </UiButton>
        </div>
      </div>
      <div class="mb-3 text-xs font-light text-gray-60">
        <div
          v-if="product?.embedded"
          v-html="product.product_short_description"
        />
        <template v-else-if="!isDiamond">
          <div class="text-xs font-light text-gray-60">
            {{ filteredProductMaterials }}{{ filteredProductOptionsMaterials }}
          </div>
        </template>

        <div v-if="ringSizeMaterial" class="mt-1 flex items-center justify-between gap-2">
          <p class="flex gap-1 text-xs">
            <span class="text-gray-60">{{ $t("size") }}:</span>
            <ProductMaterialSelectView
              v-if="ringSizeCategoryMaterials && isEditMode && !isRingSizeUpdating"
              class="materials-select"
              hide-label
              :block="ringSizeCategoryMaterials"
              :title="''"
              :selected-item="ringSizeSelectedItem"
              skip-check-default-size
              @filter="setSelectedValue"
            />
            <span v-else-if="ringSizeCategoryMaterials && isRingSizeUpdating">
              {{ ringSizeSelectedItem?.currentTranslation?.title }}
            </span>
            <span v-else>
              {{ ringSizeMaterial.title }}
            </span>
          </p>
          <UiButton
            v-if="!hideControls"
            variant="clear"
            class="shrink-0 p-0 text-xs underline disabled:border-transparent disabled:bg-transparent disabled:text-primary disabled:opacity-50"
            :loading="isRingSizeUpdating || isRingSizeFetching"
            @click="isEditMode ? updateRingSizeItem() : startEditMode()"
          >
            {{ $t(isEditMode ? "save" : "edit") }}
          </UiButton>
        </div>

        <div v-if="chainLengthMaterial" class="mt-1 flex gap-1 text-xs">
          <span class="text-gray-60">
            {{ chainLengthMaterial.category_title }}:
          </span>
          <span>
            {{ chainLengthMaterial.currentTranslation?.title || chainLengthMaterial.title }}
          </span>
        </div>
      </div>
      <div
        class="mb-3 flex flex-wrap items-center gap-2 text-left text-[15px] font-light text-orange-60"
      >
        <div
          class="flex items-center gap-1"
          data-e2e-item="cart-product-price"
        >
          <span
            v-if="Number(product?.start_at_cost || 0)"
            class="text-gray-70 line-through"
          >
            {{ $n(Number(product.start_at_cost), "currency") }}
          </span>
          {{
            $n(
              Number(product.sale ? product.sale_cost : product.base_cost),
              "currency",
            )
          }}
        </div>

        <ProductFinalSale v-if="trackInventory && !isReadyToShipCategory" class="text-gray-70" />
      </div>
      <UiNumberInput
        v-if="!hideControls"
        v-model="internalQuantity"
        input-classes="w-8 text-center"
        :min="1"
        :max="product.track_inventory ? product.stock_qty || 0 : 999"
        :disabled="
          isGiftProduct(product) ||
          isUpdating ||
          isRemoving ||
          isRingSizeUpdating
        "
      >
        <template v-if="internalQuantity === 1" #decrement>
          <UiButton
            variant="clear"
            :loading="isUpdating || isRemoving"
            class="inline-flex size-6 shrink-0 items-center justify-center rounded-full border border-gray-10 p-0 text-primary disabled:opacity-50"
            :aria-label="$t('ariaLabelRemoveItem', { title: product.title })"
            :disabled="
              isGiftProduct(product) ||
              isUpdating ||
              isRemoving ||
              isRingSizeUpdating
            "
            @click="removeCartItemWrap"
          >
            <UiIcon name="trash" class="size-3" />
          </UiButton>
        </template>
      </UiNumberInput>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CartItem, CartItemMaterials, MaterialData } from "ecom-types";
import {
  cartMaterialShowListSKU,
  chainLengthSku,
  diamondSku,
  propertiesGroupSkus,
  ringSizeSku,
} from "@/configs";

const props = defineProps<{
  product: CartItem;
  hideMenu?: boolean;
  showEstimation?: boolean;
  isChild?: boolean;
  productIndex?: number;
  hideControls?: boolean;
  wishlistInTitle?: boolean;
  isRingVariantVisible?: boolean;
  statisticItemListName?: string;
  statisticItemListId?: string;
}>();

defineEmits<{
  (e: "showMore"): void;
  (e: "remove"): void;
}>();

const { inWishlist, updateWishlist } = useWishlistV2();

const localePath = useLocalePathPolyfill();

const {
  closeCart,
  removeCartItem,
  updateCartItem,
  isGiftProduct,
  cartItems,
} = useCart();

const { removeFromCartEvent } = useEvents();

const {
  isEditMode,
  filteredMaterialsByCategories,
  startEditMode,
  updateItem: updateRingSizeItem,
  setSelectedValue,
  currentProductConfiguration,
  isUpdating: isRingSizeUpdating,
  isFetching: isRingSizeFetching,
} = useChangeCartItemConfiguration(toRef(() => props.product));

const internalQuantity = ref(props.product.quantity);
const isUpdating = ref(false);
const isRemoving = ref(false);

const unwatch = watchDebounced(
  internalQuantity,
  async () => {
    if (
      !internalQuantity.value ||
      isRemoving.value ||
      isUpdating.value
    )
      return;

    isUpdating.value = true;
    try {
      await updateCartItem({
        ...props.product,
        quantity: internalQuantity.value,
      });
    } finally {
      isUpdating.value = false;
    }
  },
  { debounce: 500 },
);

watch(
  () => props.product.quantity,
  (newQuantity) => {
    internalQuantity.value = newQuantity;
  },
);

// TODO Remove hardcode when category is not going to be used
const isReadyToShipCategory = computed(() => {
  return props.product?.categories?.some((category) => category.sku === "RTS");
});

const trackInventory = computed(() => {
  return Boolean(props.product.options?.track_inventory);
});
const productUrl = computed(() => {
  if (props.product.options.engagement) {
    if (props.product.options.engagement.product_type === "diamond") {
      return localePath({
        path: `/product/${props.product.slug}`,
        query: {
          configuration_id: props.product.configuration_id,
          pmaterials: props.product.options.engagement.pmaterials,
          dsku: props.product.options.engagement.dsku,
          psku: props.product.options.engagement.psku,
          dShape: props.product.options.engagement.dShape || "",
          dCarat: props.product.options.engagement.dCarat,
          pAllowedShapes: props.product.options.engagement.pAllowedShapes,
          pAllowedCarats: props.product.options.engagement.pAllowedCarats,
          startWith: props.product.options.engagement.startWith,
          flowType: props.product.options.engagement.flowType,
        },
      });
    } else if (props.product.options.engagement.product_type === "product") {
      return localePath({
        path: `/product/${props.product.slug}`,
        query: {
          configuration_id: props.product.configuration_id,
          materials: props.product.options.engagement.pmaterials,
          pmaterials: props.product.options.engagement.pmaterials,
          dsku: props.product.options.engagement.dsku,
          psku: props.product.options.engagement.psku,
          dShape: props.product.options.engagement.dShape || "",
          dCarat: props.product.options.engagement.dCarat,
          pAllowedShapes: props.product.options.engagement.pAllowedShapes,
          pAllowedCarats: props.product.options.engagement.pAllowedCarats,
          startWith: props.product.options.engagement.startWith,
          flowType: props.product.options.engagement.flowType,
        },
      });
    } else {
      return localePath({
        path: "/engagement-ring-settings/review",
        query: {
          psku: props.product.options.engagement.psku,
          dsku: props.product.options.engagement.dsku,
          pmaterials: props.product.options.engagement.pmaterials,
          dShape: props.product.options.engagement.dShape || "",
          dCarat: props.product.options.engagement.dCarat,
          pAllowedShapes: props.product.options.engagement.pAllowedShapes,
          pAllowedCarats: props.product.options.engagement.pAllowedCarats,
          startWith: props.product.options.engagement.startWith,
          flowType: props.product.options.engagement.flowType,
        },
      });
    }
  } else {
    return localePath(
      `/product/${props.product.slug}?configuration_id=${props.product.configuration_id}`,
    );
  }
});

const isDiamond = computed(() => {
  return !!props.product?.categories?.find((el) => el.sku === diamondSku);
});

const filteredProductMaterials = computed(() => {
  const res = props.product.materials.reduce(
    (acc, material) => {
      const constant = cartMaterialShowListSKU.find(
        (item) => item.sku === material.category_sku,
      );

      if (
        constant?.sku === propertiesGroupSkus.stoneSize ||
        constant?.sku === propertiesGroupSkus.caratWeight
      ) {
        acc.push({ title: material.title + " Ct. Tw.", order: constant.order });
      } else if (constant) {
        acc.push({ title: material.title, order: constant.order });
      }

      return acc;
    },
    [] as { title: string; order: number }[],
  );

  res.sort((a, b) => a.order - b.order);

  return res.map((item) => item.title).join(" | ");
});

const filteredProductOptionsMaterials = computed(() => {
  return "";
});

const chainLengthMaterial = computed(() => {
  return props.product?.materials?.find(
    (material) => material?.category_sku === chainLengthSku,
  );
});

const ringSizeFromProductMaterials = computed(() => {
  return props.product.materials.find(
    (material) => material.category_sku === ringSizeSku,
  );
});

const ringSizeFromProductOptionsMaterials = computed(() => {
  return props.product.options?.materials?.find(
    (material) => material.material_category_sku?.sku === ringSizeSku,
  );
});

const ringSizeMaterial = computed<CartItemMaterials | undefined>(() => {
  return (
    ringSizeFromProductMaterials.value ||
    ringSizeFromProductOptionsMaterials.value
  );
});

const ringSizeCategoryMaterials = computed(() => {
  return filteredMaterialsByCategories.value.find(
    (category) => category.sku === ringSizeSku,
  );
});

const ringSizeSelectedItem = computed((): MaterialData | null => {
  if (!ringSizeCategoryMaterials.value) return null;
  return (currentProductConfiguration.value[ringSizeCategoryMaterials.value.slug] as unknown) as MaterialData | null;
});

async function removeCartItemWrap() {
  // Add loading state immediately
  isRemoving.value = true;

  // Wait for Vue to update the UI
  await nextTick();

  try {
    await removeCartItem(
      props.product.id,
      props.product.diamond ? props.product.diamond[0].id : undefined,
    );
    unwatch();

    // Wait for cart to update - check that item is removed from cartItems
    const maxWaitTime = 5000; // 5 seconds max wait
    const checkInterval = 50; // Check every 50ms
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
      const itemStillExists = cartItems.value?.some(
        (item) => item.id === props.product.id,
      );
      if (!itemStillExists) {
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }

    removeFromCartEvent({
      product_id: props.product.product_id,
      configuration_id: props.product.configuration_id,
      collection_id: props.product?.collections_ids?.[0] || "",
      product_sku: props.product.vendor_model,
      product_price: props.product?.base_cost,
      productData: props.product,
      productIndex: props.productIndex || 0,
    });
    if (props.product.diamond?.length) {
      removeFromCartEvent({
        product_id: props.product.diamond[0].id,
        configuration_id: props.product.diamond[0].configuration_id,
        collection_id: props.product.diamond[0]?.collections_ids?.[0] || "",
        product_sku: props.product.diamond[0].vendor_model,
        product_price: props.product.diamond[0]?.base_cost,
        productData: props.product.diamond[0],
        productIndex: props.productIndex || 0,
      });
    }
  } catch (error) {
    console.error("Error removing cart item:", error);
  } finally {
    // Remove loading state only after cart is fully updated
    isRemoving.value = false;
  }
}
</script>

<style scoped>
.materials-select {
  padding: 0;
}
.materials-select :deep(select) {
  @apply h-auto border-none py-0 pl-0 text-xs;
}
</style>
